import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Capacitor } from '@capacitor/core';
import { SessionService } from '@galaxy/core';
import { BasePersona, IAMService as IAMV1Service, PersonaType } from '@vendasta/iam';
import { IAMService as IAMV2Service, UserIdentifier } from '@vendasta/iamv2';
import jwtDecode from 'jwt-decode';
import { Observable, catchError, filter, firstValueFrom, map, of, shareReplay, switchMap } from 'rxjs';
import { environment, partnerId } from '../globals';
export { User } from '@vendasta/iamv2';

export interface JWT {
  iss: string;
  exp?: number;
  sub?: string;
  temporary_subject?: {
    type: string;
    namespace?: string;
  };
  impersonatee?: string;
  user_id?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly httpClient = inject(HttpClient);

  readonly sessionId = toSignal(this.sessionService.getSessionId());
  readonly sessionData = computed(() => this.decodeJWT(this.sessionId()));
  readonly isAuthed = computed(() => this.tokenIsValid(this.sessionData()));
  readonly isInPreviewMode = computed(() => !!this.sessionData()?.temporary_subject);
  readonly impersonatee = computed(() => this.sessionData()?.impersonatee);
  readonly isImpersonating = computed(() => !!this.impersonatee());
  readonly userId = computed(() => this.sessionData()?.user_id);
  readonly effectiveUserId = computed(() => (this.isImpersonating() ? this.impersonatee() : this.userId()));

  // Note - the temporary subject represents a user that HAS NOT logged-in. The user represented by the temporary
  // subject is real, but the user's identity HAS NOT been confirmed, so the temporary session SHOULD NOT be trusted and
  // SHOULD be used cautiously.
  private temporarySubject$ = this.getLoggedInSubject(PersonaType.account_group).pipe(
    catchError(() => of(null)),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );
  temporarySubjectEmail$: Observable<string> = this.temporarySubject$.pipe(
    map((subject) => (subject ? subject?.email : null)),
  );
  temporarySubjectUserId$: Observable<string> = this.temporarySubject$.pipe(
    map((subject) => (subject ? subject?.userId : null)),
  );

  smbSubjectId$ = this.getLoggedInSubject(PersonaType.smb).pipe(
    catchError(() => of(null)),
    map((subject) => subject?.subjectId),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly currentUser$ = this.sessionService.getSessionId().pipe(
    filter((sessionId) => !!sessionId),
    switchMap((token) => this.iamV2.getUser(new UserIdentifier({ token }))),
    shareReplay(1),
  );
  readonly currentUser = toSignal(this.currentUser$);
  readonly email = computed(() => this.currentUser()?.email);

  constructor(private iamV1: IAMV1Service, private iamV2: IAMV2Service, private sessionService: SessionService) {}

  async login(email: string, password: string) {
    const iamSession = await firstValueFrom(this.iamV1.login(email, password, null, partnerId));
    this.sessionService.setSessionId(iamSession);
    if (!Capacitor.isNativePlatform()) {
      await firstValueFrom(this.createVBCSession(iamSession, partnerId));
    }
  }

  private decodeJWT(jwt: string): JWT | null {
    if (!jwt) {
      return null;
    }
    try {
      return jwtDecode(jwt);
    } catch (err) {
      console.error('Error decoding JWT', err);
    }
    return null;
  }

  private tokenIsValid(token: JWT): boolean {
    return (
      !!token && token.iss === `https://iam-${environment}.apigateway.co` && new Date() < new Date(token.exp * 1000)
    );
  }

  private getLoggedInSubject(personaType: PersonaType, partnerId?: string): Observable<BasePersona | null> {
    return this.sessionService
      .getSessionId()
      .pipe(switchMap((sessionId: string | null) => this.iamV1.getSubjectBySession(sessionId, personaType, partnerId)));
  }

  public createVBCSession(iamSession: string, partnerId = 'VUNI') {
    const headers = new HttpHeaders({
      'content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
    });
    return this.httpClient.post('/ajax/v1/create-session/', `sessionId=${iamSession}&partnerId=${partnerId}`, {
      headers: headers,
    });
  }
}
